import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

export interface CustomToggleProps {
  /**
   * Provide an id that unique represents the underlying `input`
   */
  id?: string;
  /**
   * Specify the label for the "off" position
   */
  labelA?: string;
  /**
   * Specify the label for the "on" position
   */
  labelB?: string;
  /**
   * Specify whether the toggle should be on by default
   */
  defaultToggled?: boolean;
  /**
   * Controlled toggle state
   */
  toggled?: boolean;
  /**
   * Provide a className that is applied to the toggle component
   */
  className?: string;
  /**
   * Provide a function that is called when a change event occurs on the control
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Provide an optional hook that is called when the control is toggled
   */
  onToggle?: (
    value: boolean,
    htmlFor: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  /**
   * Provide an name that unique represents the underlying `input`
   */
  name?: string;
  /**
   * Specify if the control should be disabled
   */
  disabled?: boolean;
}

/** A toggle is used to quickly switch between two possible states. They are commonly used for "on/off" switches */
const CustomToggle: React.FC<CustomToggleProps> = ({
  id,
  labelA = 'Off',
  labelB = 'On',
  defaultToggled = false,
  toggled,
  className,
  onChange,
  onToggle,
  name,
  disabled = false,
  ...other
}) => {
  const [isToggled, setIsToggled] = useState(toggled ?? defaultToggled);

  // Update internal state when controlled toggled prop changes
  useEffect(() => {
    if (typeof toggled !== 'undefined') {
      setIsToggled(toggled);
    }
  }, [toggled]);

  const htmlFor =
    id || name || `toggle-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = classNames(
    'wfp--form-item',
    'custom-toggle-wrapper',
    className,
  );

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;

    // Only update internal state if not controlled
    if (typeof toggled === 'undefined') {
      setIsToggled(newValue);
    }

    // Call the onChange callback
    if (onChange) {
      onChange(event);
    }

    // Call the onToggle callback
    if (onToggle) {
      onToggle(newValue, htmlFor, event);
    }
  };

  return (
    <div className={wrapperClasses}>
      <input
        {...other}
        type="checkbox"
        id={htmlFor}
        name={name}
        className="wfp--toggle custom-toggle"
        checked={isToggled}
        onChange={handleToggleChange}
        disabled={disabled}
      />
      <label
        className="wfp--toggle__label custom-toggle__label"
        htmlFor={htmlFor}
      >
        <span className="wfp--toggle__text--left custom-toggle__text--left">
          {labelA}
        </span>
        <span className="wfp--toggle__appearance custom-toggle__appearance" />
        <span className="wfp--toggle__text--right custom-toggle__text--right">
          {labelB}
        </span>
      </label>
    </div>
  );
};

CustomToggle.displayName = 'CustomToggle';

export default CustomToggle;
